import { OFFSET_DEFAULT } from 'app/shared/constants';
import { entitiesSearch } from 'app/modules/search/actions';
import { formatEntityOptions, ValueField } from 'app/modules/search/helpers';
import { selectHasReadEntitiesPermissions } from 'app/modules/session/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useRef } from 'react';
import { FilterMoreDisplay } from 'app/modules/filters/components/FilterMoreDisplay';
import {
  selectSearchV2Enabled,
  selectSearchV2UseDlHashIdEnabled,
} from 'app/shared/featureFlags/selectors';
import { useEntitiesForFilterDisplay } from 'app/modules/entities/queries/useEntitiesForFilterDisplay';

interface Props {
  ids: string[];
  label: string;
  valueField: ValueField;
}

export const EntityFilterDisplay = (props: Props) => {
  const { ids, label, valueField } = props;
  const dispatch = useDispatch();

  // using search/selectors caused an import cycle
  const v1Entities = useSelector(
    (state: RootState) => state.search.entities.data,
  );
  const hasReadEntitiesPermissions = useSelector(
    selectHasReadEntitiesPermissions,
  );
  const isSearchV2Enabled = useSelector(selectSearchV2Enabled);
  const isSearchV2UseDLHashIdEnabled = useSelector(
    selectSearchV2UseDlHashIdEnabled,
  );

  // Use the new hook that checks opensearch cache first, then falls back to useGetEntities
  const { entities: entitiesFromHook } = useEntitiesForFilterDisplay({
    ids,
    valueField,
  });

  const data = useMemo(
    () => (isSearchV2Enabled ? entitiesFromHook : v1Entities),
    [entitiesFromHook, isSearchV2Enabled, v1Entities],
  );

  // Map of IDs to options
  const entityOptionsMap = useMemo(
    () =>
      formatEntityOptions(
        data,
        undefined,
        valueField,
        true,
        isSearchV2UseDLHashIdEnabled,
      ).reduce(
        (acc, i) => ({
          ...acc,
          [i.value]: i,
        }),
        {},
      ),
    [data, valueField, isSearchV2UseDLHashIdEnabled],
  );

  // calledRef is used to prevent an infinite loop in case the API never returns with the missing value
  const calledRef = useRef(false);
  useEffect(() => {
    calledRef.current = false;
  }, [ids]);

  useEffect(() => {
    if (hasReadEntitiesPermissions && !isSearchV2Enabled) {
      const missingValues = ids.filter((i) => !entityOptionsMap[i]);
      if (missingValues.length && !calledRef.current) {
        calledRef.current = true;

        if (valueField === ValueField.ID) {
          dispatch(
            entitiesSearch({
              ids: missingValues,
              limit: missingValues.length,
              offset: OFFSET_DEFAULT,
              phrase: '',
              type: 'unit21_id',
            }),
          );
        } else {
          dispatch(
            entitiesSearch({
              external_ids: missingValues,
              limit: missingValues.length,
              offset: OFFSET_DEFAULT,
              phrase: '',
              type: 'external_id',
            }),
          );
        }
      }
    }
  }, [
    dispatch,
    entityOptionsMap,
    hasReadEntitiesPermissions,
    ids,
    isSearchV2Enabled,
    valueField,
  ]);

  const values = useMemo(
    () => ids.map((i) => entityOptionsMap[i]?.text || i),
    [entityOptionsMap, ids],
  );

  return (
    <>
      <b>{label}</b> is one of <FilterMoreDisplay values={values} />
    </>
  );
};
